/**
 * Shared layout tokens for Organizace / Obchody / Oblíbené directory pickers.
 *
 * Column fills CustomerAppShell main pane on lg+ (shell: CUSTOMER_CONTENT_WIDTH_CLASS
 * + lg:max-w-none). No max-w-6xl / max-w-xl here — phone max-w-xl stays on the shell.
 *
 * Seznam: inset-grouped list (iOS Settings / Slack) via DirectoryPickerGroup.
 * Karty (lg+): cap at lg 3-col (~33% per card); no xl 4-col — see DIRECTORY_CARDS_GRID_CLASS.
 * Oblíbené Seznam: 1 → lg 2 → xl 3 — see DIRECTORY_FAVORITES_LIST_GRID_CLASS.
 */

/**
 * Directory column — full main-pane width (opt-in; not JourneyShell / auth narrow).
 */
export const DIRECTORY_PICKER_COLUMN_CLASS =
  'mx-auto flex w-full min-w-0 flex-col';

/**
 * Shared outer stack for directory pages (Shops / Orgs / guest Favorites).
 * `pt-4` (1rem) matches `.customer-safe-area-x` horizontal inset so the heading
 * sits equally from the top and left of the content box.
 * Screens may append bottom clearance / flex-1 for map mode.
 * Under AccountShell (`CUSTOMER_TAB_CONTENT_CLASS` already has `py-4`), use
 * `DIRECTORY_PAGE_STACK_NESTED_CLASS` instead to avoid double top pad.
 */
export const DIRECTORY_PAGE_STACK_CLASS =
  `${DIRECTORY_PICKER_COLUMN_CLASS} gap-3 pt-4`;

/**
 * Directory stack when a parent already supplies the 1rem top inset
 * (AccountShell / JourneyShell `CUSTOMER_TAB_CONTENT_CLASS`).
 */
export const DIRECTORY_PAGE_STACK_NESTED_CLASS =
  `${DIRECTORY_PICKER_COLUMN_CLASS} gap-3`;

/** Padding inside each grouped-list (Seznam) row — Retail V1 (~16px H / 12px V). */
export const DIRECTORY_PICKER_ROW_PAD_CLASS = 'px-4 py-3 sm:px-4 sm:py-3';

/**
 * Home nearby / ☆ Seznam inset row (HUX-09 / D23).
 * Quiet divide-y peer of DirectoryPickerGroup — **not** bordered card-gap cells.
 * Pair with `DirectoryResults` `listLayout="inset"`. Target row 68–82px with
 * md thumb (`--directory-thumb-size` 52–60).
 */
export const DIRECTORY_NEARBY_INSET_ROW_CLASS = [
  'min-h-[4.25rem]',
  DIRECTORY_PICKER_ROW_PAD_CLASS,
].join(' ');

/**
 * Oblíbené / Obchody Seznam split grid — 1-col → lg 2-col (~50%) →
 * xl 3-col (~33%). Midpoint at lg keeps one-line row cards readable once the
 * sidebar appears; 3-col waits until xl when the main pane is wide enough.
 */
export const DIRECTORY_FAVORITES_LIST_GRID_CLASS =
  'grid w-full grid-cols-1 items-stretch gap-3 lg:grid-cols-2 xl:grid-cols-3';

/**
 * Organizace picker (Seznam + Karty) — 1 → sm 2 → lg 3 per row (~33% max on lg+).
 * lg aligns with `DIRECTORY_LG_MQ` / Karty density toggle; no 4th column at xl.
 */
export const DIRECTORY_ORG_PICKER_GRID_CLASS =
  'grid w-full grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3';

/**
 * Objednávky Seznam/Karty — gradual split like Favorites Seznam (not dense Karty lg 3-col).
 * 1 col compact → lg 2-col → xl 3-col; CTAs + pickup copy still fit at xl.
 */
export const DIRECTORY_ORDERS_GRID_CLASS =
  'grid w-full grid-cols-1 items-stretch gap-3 lg:grid-cols-2 xl:grid-cols-3';

/**
 * Objednávky search — capped width on sm+ so sort/org/density controls keep room.
 * Mobile keeps 55% beside Filtry; desktop steps up to 20rem max.
 */
export const DIRECTORY_ORDERS_SEARCH_FIELD_CLASS = [
  'w-full min-w-0',
  'max-w-[55%] flex-[3] basis-[55%]',
  'sm:max-w-[16rem] sm:flex-none sm:basis-auto',
  'lg:max-w-[18rem]',
  'xl:max-w-[20rem]',
].join(' ');

/**
 * Objednávky card/list actions — see customer.extensions.css breakpoint rule:
 * count 1 → full width below CQ; half-width @container ≥ 22rem (`.rp-order-actions--solo`);
 * count 2 → always row (`.rp-order-actions--pair`); 3+ → row @container ≥ 22rem.
 */
export const DIRECTORY_ORDERS_ACTIONS_CLASS = 'rp-order-actions';

/** Solo modifier — one CTA; half-width when the card container is wide enough. */
export const DIRECTORY_ORDERS_ACTIONS_SOLO_CLASS = 'rp-order-actions--solo';

/** Pair modifier — two CTAs always side-by-side (no container-query gate). */
export const DIRECTORY_ORDERS_ACTIONS_PAIR_CLASS = 'rp-order-actions--pair';

/** Inline style key — column count for strict 1×N / N×1 grid (never 2+1). */
export const RP_ORDER_ACTION_COUNT_CSS_VAR = '--rp-order-action-count';

/** Resolves Objednávky actions shell class from visible CTA count. */
export function resolveOrderActionsClass(actionCount: number): string {
  if (actionCount === 1) {
    return `${DIRECTORY_ORDERS_ACTIONS_CLASS} ${DIRECTORY_ORDERS_ACTIONS_SOLO_CLASS}`;
  }
  if (actionCount === 2) {
    return `${DIRECTORY_ORDERS_ACTIONS_CLASS} ${DIRECTORY_ORDERS_ACTIONS_PAIR_CLASS}`;
  }
  return DIRECTORY_ORDERS_ACTIONS_CLASS;
}

/**
 * Max width of one Seznam / empty-panel cell — matches list split + `gap-3`
 * (lg 2-col / xl 3-col). Full bleed below lg; left-aligned on lg+.
 */
export const DIRECTORY_ONE_LIST_CARD_MAX_CLASS = [
  'mr-auto w-full min-w-0 max-w-full',
  'lg:max-w-[calc((100%-0.75rem)/2)]',
  'xl:max-w-[calc((100%-1.5rem)/3)]',
].join(' ');

/** Organizace picker cell — fill one grid track (DIRECTORY_ORG_PICKER_GRID_CLASS). */
export const DIRECTORY_ORG_PICKER_ITEM_CLASS = 'flex h-full w-full min-w-0 flex-col';

/**
 * Max width of one Karty cell — matches DIRECTORY_CARDS_GRID_CLASS
 * (lg+ 3-col, gap-3 ≈ 33% per card).
 */
export const DIRECTORY_ONE_DENSE_CARD_MAX_CLASS = [
  'mr-auto w-full min-w-0 max-w-full',
  'lg:max-w-[calc((100%-1.5rem)/3)]',
].join(' ');

/**
 * Sparse empty-state panels (Obchody / Organizace empty cards).
 * Full bleed below lg; lg+ cap at one dense Karty cell (~33% of main pane).
 */
export const DIRECTORY_EMPTY_PANEL_MAX_CLASS = DIRECTORY_ONE_DENSE_CARD_MAX_CLASS;

/**
 * Guest Oblíbené empty path (empty card + sign-in banner): full width below lg;
 * lg+ cap at one dense Karty cell (~33% of main pane), left-aligned in the pane.
 */
export const DIRECTORY_EMPTY_PANEL_FULL_CLASS = DIRECTORY_ONE_DENSE_CARD_MAX_CLASS;

/** `empty` = guest Oblíbené empty path; `list` / `cards` = populated directory density. */
export type DirectoryEmptyPanelDensity = 'list' | 'cards' | 'empty';

/**
 * Max-width for empty-state panels and guest sign-in banner — tracks directory density.
 * `empty` → full column; `list` → one Seznam cell; `cards` → one dense Karty cell (lg 3-col).
 */
export function resolveEmptyPanelClass(density: DirectoryEmptyPanelDensity): string {
  if (density === 'empty') {
    return DIRECTORY_EMPTY_PANEL_FULL_CLASS;
  }
  return density === 'cards'
    ? DIRECTORY_ONE_DENSE_CARD_MAX_CLASS
    : DIRECTORY_ONE_LIST_CARD_MAX_CLASS;
}

/**
 * Max-width of one populated directory cell (banner / orphan panel beside a grid).
 * `cards` → DIRECTORY_ONE_DENSE_CARD_MAX_CLASS (lg 3-col; no xl 4-col tier).
 */
export function resolveDirectoryOneCellMaxClass(
  density: Exclude<DirectoryEmptyPanelDensity, 'empty'>,
): string {
  return density === 'cards'
    ? DIRECTORY_ONE_DENSE_CARD_MAX_CLASS
    : DIRECTORY_ONE_LIST_CARD_MAX_CLASS;
}

/**
 * Oblíbené Seznam cell — bordered row card (standalone; not inset divide-y group).
 */
export const DIRECTORY_FAVORITES_LIST_ITEM_CLASS = [
  'flex h-full min-w-0 flex-col overflow-hidden rounded-lg',
  'border border-[var(--color-border)]',
  'bg-[var(--color-surface-elevated)]',
  DIRECTORY_PICKER_ROW_PAD_CLASS,
  'transition-colors duration-150 motion-reduce:transition-none',
  'hover:bg-[var(--color-surface-hover)] active:bg-[var(--color-surface-muted)]',
].join(' ');

/**
 * Objednávky list card — CQ host for `.rp-order-actions` (see customer.extensions.css).
 * Binary layout only: all CTAs stacked, or all in one row — never 2+1 wrap.
 */
export const DIRECTORY_ORDER_CARD_ITEM_CLASS = [
  DIRECTORY_FAVORITES_LIST_ITEM_CLASS,
  'rp-order-card',
].join(' ');

/**
 * Karty grid — denser columns so each cell is less wide / more portrait.
 * Below lg `useDirectoryDensity` forces Seznam, so multi-col only with density=cards on lg+.
 */
export const DIRECTORY_CARDS_GRID_CLASS =
  'grid w-full grid-cols-1 items-stretch gap-3 lg:grid-cols-3';

/**
 * Elevated card cell surface for Karty mode (`<li>`).
 * Hover/active live on the `<li>` so the whole bordered cell tints (not an inner inset).
 */
export const DIRECTORY_CARD_ITEM_CLASS = [
  'flex h-full min-h-[12.5rem] min-w-0 flex-col overflow-hidden rounded-lg',
  'border border-[var(--color-border)]',
  'bg-[var(--color-surface-elevated)]',
  'shadow-none',
  'transition-colors duration-150 motion-reduce:transition-none',
  'hover:bg-[var(--color-surface-hover)] active:bg-[var(--color-surface-muted)]',
  'has-[[data-directory-row-press]:disabled]:hover:bg-[var(--color-surface-elevated)]',
  'has-[[data-directory-row-press]:disabled]:active:bg-[var(--color-surface-elevated)]',
].join(' ');

/**
 * Inner padding for Karty card content / skeleton.
 * DirectoryEntityRow stack+press bleeds with matching `-m-4 p-4` so the stretch
 * press covers the full cell; hover color is owned by DIRECTORY_CARD_ITEM_CLASS.
 */
export const DIRECTORY_CARD_PAD_CLASS = 'p-4 sm:p-4';

/** Quiet secondary action (Browse / Donate) under org identity. */
export const DIRECTORY_PICKER_TEXT_ACTION_CLASS = [
  'text-sm text-[var(--color-on-surface-muted)]',
  'underline-offset-2 hover:text-[var(--color-on-surface)] hover:underline',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');
